import {
  useState,
  memo,
  forwardRef,
  type ReactNode,
  useEffect,
  useCallback,
  useMemo,
  type ElementType,
  type CSSProperties,
  type MutableRefObject,
  type Ref,
} from 'react';
import useFacebook from '../hooks/useFacebook';

export type ParserProps = {
  className: string;
  as?: ElementType;
  children?: ReactNode;
  style?: CSSProperties;
  data?: Record<string, string | undefined>;
};

function Parser(props: ParserProps, forwardedRef: Ref<HTMLElement>) {
  const { as: As = 'div', children, className, style, data } = props;

  const { parse } = useFacebook();
  const [element, setElement] = useState<HTMLElement | null>(null);

  const handleRef = useCallback(
    (element: HTMLElement | null) => {
      setElement(element);

      if (forwardedRef) {
        if (typeof forwardedRef === 'function') {
          forwardedRef(element);
        } else if (forwardedRef) {
          (forwardedRef as MutableRefObject<HTMLElement | null>).current = element;
        }
      }
    },
    [forwardedRef],
  );

  const key = useMemo(() => {
    let uniqueKey = `fb-parser-${As}-${className}`;
    if (!data) {
      return uniqueKey;
    }

    for (const key in data) {
      const value = data[key];
      if (value !== undefined) {
        uniqueKey += `-${key}:${data[key] || ''}`;
      }
    }

    return uniqueKey;
  }, [data, As, className]);

  useEffect(() => {
    if (element?.parentNode) {
      parse(element.parentNode as HTMLElement);
    }
  }, [element, parse, key]);

  return (
    <As className={className} style={style} ref={handleRef} {...data}>
      {children}
    </As>
  );
}

export default memo(forwardRef(Parser));
